'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useSession } from 'next-auth/react';
import React, { useCallback, useMemo, useState } from 'react';
import {
  ArticleReferenceTooltipWrapper,
  type ReferencedArticle,
} from '@/components/ui/ArticleReferenceTooltip';
import { EnablingClauseLink } from '@/components/ui/EnablingClauseLink';
import { KategoriDendaText } from '@/components/ui/KategoriDendaText';
import { TextHighlighter } from '@/components/ui/TextHighlighter';
import {
  type CitingRegulation,
  detectEnablingClauses,
  findMatchingCitingRegulation,
} from '@/lib/enabling-clause';

interface ArticleReferenceTextProps {
  text: string;
  regulationId: string;
  consolidationId?: string;
  query?: string;
  className?: string;
  highlightClassName?: string;
  citingRegulations?: CitingRegulation[];
  hasPremiumRole?: boolean;
}

type ArticleCache = Map<string, ReferencedArticle | 'loading' | 'not-found' | 'error'>;

// Regex pattern to match article references
// Matches "Pasal 29", "pasal 31", "Pasal 12A", etc.
//
// IMPORTANT: Excludes "Pasal X" that are part of external regulation citations.
// Examples of patterns that should NOT be treated as internal references:
// - "Pasal 5 ayat (3) huruf b dan huruf c Undang-Undang Darurat Nomor 1 Tahun 1951..."
// - "Pasal 1 dan Pasal 2 Undang-Undang Darurat Nomor 12 Tahun 1951..."
//
// Strategy: First find all potential "Pasal X" matches, then filter out those
// that are followed by regulation type keywords (Undang-Undang, UU, Peraturan, PP, Perpres)
// which indicates an external regulation citation.
const ARTICLE_REFERENCE_PATTERN = /\bPasal\s+(\d+[A-Za-z]?)\b/gi;

/**
 * ArticleReferenceText component
 *
 * Detects article references (e.g., "Pasal 29", "Pasal 31A") within text,
 * makes them clickable, and shows referenced article content in tooltips.
 *
 * Features:
 * - Regex-based detection of "Pasal X" patterns (case-insensitive)
 * - Fetches referenced articles on click (lazy loading)
 * - Caches fetched articles to avoid redundant API calls
 * - Handles loading, error, and not-found states
 * - Supports search highlighting alongside reference detection
 * - For logged-in users only (references appear as plain text for guests)
 */
export function ArticleReferenceText({
  text,
  regulationId,
  consolidationId,
  query,
  className = '',
  highlightClassName,
  citingRegulations = [],
  hasPremiumRole = false,
}: ArticleReferenceTextProps) {
  const { data: session } = useSession();
  const isAuthenticated = !!session?.user;
  const [articleCache, setArticleCache] = useState<ArticleCache>(new Map());
  const [openTooltipId, setOpenTooltipId] = useState<string | null>(null);

  const canShowEnablingClauseLinks = hasPremiumRole && citingRegulations.length > 0;

  const enablingClauseMatches = useMemo(() => {
    if (!canShowEnablingClauseLinks) return [];
    return detectEnablingClauses(text);
  }, [text, canShowEnablingClauseLinks]);

  const matchedEnablingClauses = useMemo(() => {
    if (!canShowEnablingClauseLinks || enablingClauseMatches.length === 0) {
      return new Map<number, { regulationId: string; linkText: string }>();
    }

    const matches = new Map<number, { regulationId: string; linkText: string }>();

    for (const match of enablingClauseMatches) {
      const matched = findMatchingCitingRegulation(
        match.topic,
        match.delegateType,
        citingRegulations
      );
      if (matched) {
        matches.set(match.startIndex, {
          regulationId: matched.regulationId,
          linkText: match.delegateRawText,
        });
      }
    }

    return matches;
  }, [canShowEnablingClauseLinks, enablingClauseMatches, citingRegulations]);

  /**
   * Helper function to update cache entries and trigger re-renders
   * Creates a new Map to ensure React detects the state change
   */
  const updateCache = useCallback(
    (key: string, value: ReferencedArticle | 'loading' | 'not-found' | 'error') => {
      setArticleCache((prev) => new Map(prev).set(key, value));
    },
    []
  );

  /**
   * Fetches a referenced article by number
   * Uses caching to avoid redundant fetches
   */
  const fetchArticle = useCallback(
    async (articleNumber: string): Promise<ReferencedArticle | null> => {
      // Check cache first
      const cached = articleCache.get(articleNumber);
      if (cached) {
        if (cached === 'loading') return null; // Already loading
        if (cached === 'not-found' || cached === 'error') return null;
        return cached;
      }

      // Set loading state
      updateCache(articleNumber, 'loading');

      try {
        const url = consolidationId
          ? `/api/consolidation/${consolidationId}/article/${articleNumber}`
          : `/api/regulation/${regulationId}/article/${articleNumber}`;
        const res = await fetch(url);

        if (!res.ok) {
          if (res.status === 404) {
            updateCache(articleNumber, 'not-found');
            return null;
          }
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        if (data.success && data.data) {
          let sourceRegulation;
          let previousSourceRegulation;
          let history;

          if (consolidationId && data.data) {
            if (data.data.lastChange) {
              sourceRegulation = data.data.lastChange.regulationTitle;
            } else if (data.data.origin) {
              sourceRegulation = data.data.origin.regulationTitle;
            }

            if (data.data.changedBy && data.data.changedBy.length > 1) {
              previousSourceRegulation =
                data.data.changedBy[data.data.changedBy.length - 2].regulationTitle;
            } else if (
              data.data.changedBy &&
              data.data.changedBy.length === 1 &&
              data.data.origin
            ) {
              previousSourceRegulation = data.data.origin.regulationTitle;
            }

            if (data.data.changedBy && data.data.changedBy.length > 0) {
              history = data.data.changedBy.map((c: any) => ({
                regulationTitle: c.regulationTitle,
                kind: c.data.kind,
              }));
            }
          }

          const article: ReferencedArticle = {
            articleNumber: data.data.articleNumber,
            articleContent: data.data.content || data.data.articleContent,
            sourceRegulation,
            previousSourceRegulation,
            history,
          };
          updateCache(articleNumber, article);
          return article;
        }

        updateCache(articleNumber, 'not-found');
        return null;
      } catch (error) {
        console.error(`Error fetching article ${articleNumber}:`, error);
        updateCache(articleNumber, 'error');
        return null;
      }
    },
    [updateCache, regulationId, consolidationId]
  );

  /**
   * Handles click on article reference
   * Triggers fetch and opens tooltip
   */
  const handleReferenceClick = useCallback(
    async (referenceId: string, articleNumber: string) => {
      setOpenTooltipId(referenceId);

      if (!isAuthenticated) {
        return;
      }

      const cached = articleCache.get(articleNumber);

      if (cached && typeof cached === 'object') {
        return;
      }

      if (cached === 'not-found' || cached === 'error') {
        return;
      }

      await fetchArticle(articleNumber);
    },
    [isAuthenticated, articleCache, fetchArticle]
  );

  const isFollowedByRegulationKeyword = (position: number): boolean => {
    const textAfter = text.substring(position, position + 300);
    return /(?:Undang-Undang|UU|Peraturan|PP|Perpres)\s+(?:Nomor|No\.?)/i.test(textAfter);
  };

  const renderContent = () => {
    type SegmentType = 'text' | 'article-ref' | 'enabling-clause';
    interface Segment {
      text: string;
      type: SegmentType;
      startIndex: number;
      endIndex: number;
      articleNumber?: string;
      enablingClauseData?: { regulationId: string; linkText: string };
    }

    const segments: Segment[] = [];

    ARTICLE_REFERENCE_PATTERN.lastIndex = 0;

    const articleRefs: Array<{
      start: number;
      end: number;
      articleNumber: string;
      text: string;
    }> = [];

    let match;
    while ((match = ARTICLE_REFERENCE_PATTERN.exec(text)) !== null) {
      const [fullMatch, articleNumber] = match;
      const matchStart = match.index;
      const matchEnd = matchStart + fullMatch.length;

      if (isFollowedByRegulationKeyword(matchEnd)) {
        continue;
      }

      articleRefs.push({
        start: matchStart,
        end: matchEnd,
        articleNumber,
        text: fullMatch,
      });
    }

    const allBreaks: Array<{
      index: number;
      type: 'article-ref' | 'enabling-clause';
      end: number;
      data?: any;
    }> = [];

    for (const ref of articleRefs) {
      allBreaks.push({
        index: ref.start,
        type: 'article-ref',
        end: ref.end,
        data: { articleNumber: ref.articleNumber, text: ref.text },
      });
    }

    for (const ecMatch of enablingClauseMatches) {
      const matched = matchedEnablingClauses.get(ecMatch.startIndex);
      if (matched) {
        allBreaks.push({
          index: ecMatch.startIndex,
          type: 'enabling-clause',
          end: ecMatch.endIndex,
          data: {
            text: ecMatch.fullMatch,
            regulationId: matched.regulationId,
            linkText: matched.linkText,
          },
        });
      }
    }

    allBreaks.sort((a, b) => a.index - b.index);

    const filteredBreaks: typeof allBreaks = [];
    let lastEnd = 0;

    for (const brk of allBreaks) {
      if (brk.index >= lastEnd) {
        filteredBreaks.push(brk);
        lastEnd = brk.end;
      }
    }

    let lastIndex = 0;

    for (const brk of filteredBreaks) {
      if (brk.index > lastIndex) {
        segments.push({
          text: text.slice(lastIndex, brk.index),
          type: 'text',
          startIndex: lastIndex,
          endIndex: brk.index,
        });
      }

      segments.push({
        text: brk.data.text,
        type: brk.type,
        startIndex: brk.index,
        endIndex: brk.end,
        articleNumber: brk.data.articleNumber,
        enablingClauseData: brk.data.regulationId
          ? { regulationId: brk.data.regulationId, linkText: brk.data.linkText }
          : undefined,
      });

      lastIndex = brk.end;
    }

    if (lastIndex < text.length) {
      segments.push({
        text: text.slice(lastIndex),
        type: 'text',
        startIndex: lastIndex,
        endIndex: text.length,
      });
    }

    if (segments.length === 0) {
      return (
        <KategoriDendaText
          text={text}
          renderText={(part, key) => (
            <TextHighlighter
              key={key}
              text={part}
              query={query || ''}
              className={className}
              highlightClassName={highlightClassName}
            />
          )}
        />
      );
    }

    return segments.map((segment, index) => {
      if (segment.type === 'enabling-clause' && segment.enablingClauseData) {
        return (
          <EnablingClauseLink
            key={`ec-${index}`}
            regulationId={segment.enablingClauseData.regulationId}
          >
            {segment.text}
          </EnablingClauseLink>
        );
      }

      if (segment.type === 'article-ref' && segment.articleNumber) {
        const referenceId = `ref-${index}-${segment.articleNumber}`;
        const cachedArticle = articleCache.get(segment.articleNumber);
        const isLoading = cachedArticle === 'loading';
        const hasError = cachedArticle === 'error';
        const notFound = cachedArticle === 'not-found';
        const article = typeof cachedArticle === 'object' ? cachedArticle : null;
        const isOpen = openTooltipId === referenceId;

        return (
          <ArticleReferenceTooltipWrapper
            key={referenceId}
            isOpen={isOpen}
            onOpenChange={(open) => {
              if (open) {
                handleReferenceClick(referenceId, segment.articleNumber!);
              } else {
                setOpenTooltipId(null);
              }
            }}
            articleNumber={segment.articleNumber}
            isAuthenticated={isAuthenticated}
            isLoading={isLoading}
            hasError={hasError}
            notFound={notFound}
            article={article}
          >
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleReferenceClick(referenceId, segment.articleNumber!);
              }}
              className="text-blue-600 underline decoration-dotted decoration-blue-400 underline-offset-2 hover:text-blue-800 hover:decoration-solid cursor-pointer transition-colors"
            >
              {segment.text}
            </button>
          </ArticleReferenceTooltipWrapper>
        );
      }

      return (
        <KategoriDendaText
          key={`text-${index}`}
          text={segment.text}
          renderText={(part, key) => (
            <TextHighlighter key={key} text={part} query={query || ''} highlightClassName={highlightClassName} />
          )}
        />
      );
    });
  };

  return (
    <TooltipPrimitive.Provider>
      <span className={className}>{renderContent()}</span>
    </TooltipPrimitive.Provider>
  );
}
